// Campaigns page — primary nav entry for the campaign-driven ad-gen flow.
//
// Lists synced campaigns from Campaign collection (populated by
// services/campaignSyncService → metaAdsCampaignService /
// googleAdsCampaignService adapters). The picker save fires an
// initial sync; "Sync now" triggers a re-fetch on demand.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Card, CardBody, VStack, HStack, Text, Button, Badge, Box, Spinner, Image, Progress,
  Tooltip, useToast, useDisclosure, SimpleGrid, Select, Input, InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson } from '../../auth/apiFetch';
import { NewCampaignModal } from './NewCampaignModal';
import { useAdReadiness, type AdReadiness } from '../../brand/useAdReadiness';
import { useBrand } from '../../brand/BrandContext';
import {
  AdSectionGrid, AdDetailModal, AdGroupModeToggle, sectionFor, resolveAndOpenMaster,
  DEFAULT_AD_GROUP_MODE, type AdGroupMode, type ExpansionAd
} from '../ProductAds';
import { toExpansionAd } from '../../lib/expansionAd';
import { campaignArmLabel } from '../../marketingPlans/client';
import { useCampaignPlanScope } from './usePlanScope';

// Legacy Campaign / CampaignInsights types were retired with the
// product-ads-style redesign — the new endpoint /api/campaigns/ads-summary
// returns CampaignSummary + CampaignRowData (defined below). The legacy
// /api/campaigns endpoint still serves the original projection for the
// detail page consumer.

// Shape returned by GET /api/campaigns/ads-summary — drives the new
// product-ads-style table layout.
type CampaignSummary = {
  totalCampaigns:      number;
  campaignsWithAds:    number;
  campaignCoveragePct: number;
  adsCreated:          number;
  adsReadyToExport:    number;
  goodOpportunities:   number | null;
  // Outcome split, added alongside the coverage fix. `adsCreated` counts
  // ATTEMPTS and stays that way — the untruth was calling a campaign covered,
  // not calling ads created. Optional: the backend PR lands first, and every
  // read is guarded so this page renders unchanged against the older shape.
  adsDelivered?:       number;
  adsFailed?:          number;
  adsInFlight?:        number;
  campaignsAttemptedNoneDelivered?: number;
};

// Per-campaign expansion payload from GET /api/campaigns/:id/ads-detail.
// Mirrors the catalog ads-detail response but with a products sidebar
// (instead of campaigns sidebar like ProductAds).
type CampaignAdsProduct = {
  productId: string;
  title:     string;
  imageUrl:  string | null;
  price:     number | null;
  currency:  string | null;
  adCount:   number;
};
// ads come from GET /api/campaigns/:id/ads-detail via the shared
// toExpansionAd adapter (same function Product Ads / Campaign Detail /
// UGC Ads / the agent card use). That projection already includes
// renderStage/renderStageAt and phase/failure. It does NOT currently
// emit visionQc or renderErrorMessage (unlike catalog.js ads-detail /
// projectAd()). Those stay undefined here until a backend follow-up;
// the adapter forwards them when present and does not fabricate them.
type CampaignAdsDetail = {
  products: CampaignAdsProduct[];
  ads:      ExpansionAd[];
};

type CampaignRowData = {
  campaignId:        string;
  name:              string;
  status:            string | null;
  kind:              string | null;
  platform:          string | null;
  thumbUrl:          string | null;
  productCount:      number;
  productsWithAds:   number;
  ugcCount:          number;
  adCount:           number;
  readyToExport:     number;
  coveragePct:       number;
  // Outcome split from the coverage fix; optional so the row renders unchanged
  // against a backend that predates it.
  deliveredCount?:   number;
  failedCount?:      number;
  inFlightCount?:    number;
  channels:          string[];
  opportunityScore:  number | null;
  lastActivityAt:    string | null;
  lastActivityLabel: string;
  isExpired:         boolean;
  // Optional plan linkage. Absent on older ads-summary payloads.
  // Platform id. Used only to match a plan's campaigns. Not rendered.
  externalId?:       string | null;
};

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60)   return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60)   return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)    return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

// Coverage now counts DELIVERED creatives, not bare Ad rows (see the backend's
// services/adDeliveryCounts.js). That fixes "all 12 ads failed but the campaign
// reads covered", but it would introduce a NEW untruth at the bottom of the
// scale if this stayed as-is: a campaign whose every ad failed lands on pct 0
// and would be labelled "No ads", which is equally false and hides the
// failures. The zero case is therefore split by OUTCOME.
//
// Kept deliberately in sync with the same function in pages/ProductAds — the
// two pages are siblings on this data and a divergence here is how the
// dashboards start disagreeing with each other.
function coverageLabel(
  pct: number,
  counts?: { attempted?: number; inFlight?: number }
): { label: string; color: string } {
  if (pct >= 80) return { label: 'Excellent', color: 'green' };
  if (pct >= 60) return { label: 'Good',      color: 'green' };
  if (pct >= 30) return { label: 'Moderate',  color: 'orange' };
  if (pct >  0)  return { label: 'Low',       color: 'orange' };
  // pct === 0 — say WHY there is no coverage.
  if ((counts?.inFlight ?? 0) > 0)  return { label: 'Generating',        color: 'orange' };
  if ((counts?.attempted ?? 0) > 0) return { label: 'Nothing delivered', color: 'red'    };
  return { label: 'No ads', color: 'gray' };
}

// Compose the tooltip body when ad-readiness blocks a button. Lists
// each blocker on its own line. Shown via Chakra Tooltip when the user
// hovers the disabled trigger.
function readinessTooltip(r: AdReadiness): string {
  if (r.ready) return '';
  const lines = (r.blockers || []).map(b => `• ${b.message}`);
  return [r.reason, ...lines].filter(Boolean).join('\n');
}

export function CampaignsPage() {
  const toast = useToast();
  const newCampaignModal = useDisclosure();
  const [params, setParams] = useSearchParams();
  const readiness = useAdReadiness();
  const gateDisabled = !readiness.ready;
  const gateTip = readinessTooltip(readiness);

  // Auto-open the New Campaign modal when the page is reached via
  // /campaigns?new=1 (e.g. the Media Library's "Add to Campaign →
  // + New campaign…" affordance). Strip the param after opening so
  // a refresh doesn't re-trigger.
  useEffect(() => {
    if (params.get('new') === '1') {
      newCampaignModal.onOpen();
      const next = new URLSearchParams(params);
      next.delete('new');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { activeBrand } = useBrand();
  const activeBrandId = activeBrand?.id ?? null;
  const navigate = useNavigate();

  const [summary, setSummary]   = useState<CampaignSummary | null>(null);
  const [rows, setRows]         = useState<CampaignRowData[]>([]);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [error,   setError]     = useState<string | null>(null);

  // Filters (search + 3 dropdowns) — applied client-side over the
  // rows. The endpoint returns everything for the brand; with hundreds
  // of campaigns we can revisit server-side pagination + filter.
  const [search, setSearch]                   = useState('');
  const [statusFilter, setStatusFilter]       = useState('');
  const [channelFilter, setChannelFilter]     = useState('');
  const [oppFilter, setOppFilter]             = useState('');
  const planScope = useCampaignPlanScope(activeBrandId, rows);

  // Per-row Refresh-Ads in-flight tracking — keyed by campaignId so
  // multiple Refresh clicks don't collide with each other.
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());

  // Row expansion — lazy-load /api/campaigns/:id/ads-detail on first
  // expand, cache in state for the session. Mirrors ProductAds expansion.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expansions, setExpansions] = useState<Record<string, CampaignAdsDetail>>({});
  const [expansionLoading, setExpansionLoading] = useState<Record<string, boolean>>({});

  // Ad detail modal — single state across the whole page so any
  // expansion's thumbnail opens the same modal.
  const [openAd, setOpenAd] = useState<ExpansionAd | null>(null);
  // Late-arrival guard — every other AdDetailModal mount (Product Ads,
  // Campaign Detail, UGC Ads) already has this; a copy-save or regen that
  // resolves after the operator closed/switched ads must not silently
  // reopen a closed modal or overwrite a since-opened different ad. This
  // page was the one surface missing it.
  const openAdIdRef = useRef<string | null>(null);
  openAdIdRef.current = openAd?.adId ?? null;

  const refresh = useCallback(async () => {
    if (!activeBrandId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiJson<{ summary: CampaignSummary; campaigns: CampaignRowData[] }>(
        `/api/campaigns/ads-summary?brandId=${encodeURIComponent(activeBrandId)}`
      );
      setSummary(res.summary);
      setRows(res.campaigns || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [activeBrandId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Filter the rows client-side. Status / channel / opportunity-bucket
  // dropdowns combine with the search input via AND.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return planScope.scopedRows.filter(r => {
      if (q && !r.name.toLowerCase().includes(q)) return false;
      if (statusFilter) {
        const status = (r.status || '').toUpperCase();
        if (statusFilter === 'ACTIVE'  && !/ACTIVE|ENABLED/.test(status)) return false;
        if (statusFilter === 'PAUSED'  && !/PAUSED/.test(status))         return false;
        if (statusFilter === 'EXPIRED' && !r.isExpired)                   return false;
      }
      if (channelFilter && !r.channels.includes(channelFilter))          return false;
      if (oppFilter) {
        const s = r.opportunityScore;
        if (oppFilter === 'strong'   && !(s != null && s >= 70))         return false;
        if (oppFilter === 'moderate' && !(s != null && s >= 40 && s < 70)) return false;
        if (oppFilter === 'low'      && !(s != null && s < 40))          return false;
        if (oppFilter === 'unscored' && s != null)                       return false;
      }
      return true;
    });
  }, [planScope.scopedRows, search, statusFilter, channelFilter, oppFilter]);

  function clearFilters() {
    setSearch('');
    setStatusFilter('');
    setChannelFilter('');
    setOppFilter('');
    planScope.setPlanFilter('');
  }

  const generateForCampaign = (campaignId: string) => {
    navigate(`/generate-ads?campaignId=${encodeURIComponent(campaignId)}`);
  };

  // Lazy-load + toggle the per-row expansion. Cached across the
  // session so a re-expand doesn't refetch.
  const expandRow = async (campaignId: string) => {
    const newId = expandedId === campaignId ? null : campaignId;
    setExpandedId(newId);
    if (newId && !expansions[campaignId] && activeBrandId) {
      setExpansionLoading(prev => ({ ...prev, [campaignId]: true }));
      try {
        const res = await apiJson<CampaignAdsDetail>(
          `/api/campaigns/${encodeURIComponent(campaignId)}/ads-detail?brandId=${encodeURIComponent(activeBrandId)}`
        );
        setExpansions(prev => ({
          ...prev,
          [campaignId]: { ...res, ads: (res.ads || []).map(toExpansionAd) }
        }));
      } catch (e) {
        toast({
          title:       'Could not load campaign ads',
          description: e instanceof Error ? e.message : String(e),
          status:      'error',
          duration:    5000
        });
      } finally {
        setExpansionLoading(prev => ({ ...prev, [campaignId]: false }));
      }
    }
  };

  const refreshAdsForCampaign = async (campaignId: string) => {
    setRefreshing(prev => { const next = new Set(prev); next.add(campaignId); return next; });
    try {
      const res = await apiJson<{ campaignRunId: string }>('/api/ads/runs', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ campaignId })
      });
      toast({
        title:       'Refresh started',
        description: 'Draining queued ads — opening run progress.',
        status:      'success',
        duration:    2500
      });
      // campaignId is already this function's own parameter — thread it
      // through so /ads scopes its query to this campaign instead of an
      // unscoped, generatedAt-sorted, capped list (same bug/fix as the
      // wizard's Step4Generate.tsx redirect). runBrandId isn't needed here:
      // this campaign always belongs to the currently-active brand (this
      // page only lists campaigns for activeBrandId), unlike the wizard
      // redirect where a mismatch is possible.
      navigate(`/ads?campaignRunId=${encodeURIComponent(res.campaignRunId)}&campaignId=${encodeURIComponent(campaignId)}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // 422 means no queued ads left — common, treat as info not error.
      const isEmpty = /no queued ads remaining/i.test(msg);
      toast({
        title:       isEmpty ? 'Nothing queued' : 'Refresh failed',
        description: isEmpty ? 'This campaign has no queued ads to render. Use Generate Ads to queue more.' : msg,
        status:      isEmpty ? 'info' : 'error',
        duration:    isEmpty ? 4000 : 6000
      });
    } finally {
      setRefreshing(prev => { const next = new Set(prev); next.delete(campaignId); return next; });
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const results = await Promise.allSettled([
        apiJson('/api/integrations/meta-ads/sync-campaigns',  { method: 'POST' }),
        apiJson('/api/integrations/google-ads/sync-campaigns', { method: 'POST' })
      ]);
      const okCount = results.filter(r => r.status === 'fulfilled').length;
      toast({
        title: okCount > 0 ? 'Sync started' : 'No active integrations',
        description: okCount > 0
          ? 'Campaigns are refreshing from connected ad platforms.'
          : 'Connect Meta Ads or Google Ads to sync campaigns.',
        status: okCount > 0 ? 'success' : 'info',
        duration: 3500
      });
      await refresh();
    } finally {
      setSyncing(false);
    }
  };

  if (!activeBrandId) {
    return (
      <VStack align="stretch" spacing={4}>
        <PageHeader title="Campaigns" description="Manage campaign-level ad generation and exports at scale." />
        <Card variant="outline"><CardBody><Text fontSize="sm" color="brand.muted">Select a brand to continue.</Text></CardBody></Card>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" align="flex-start">
        <PageHeader title="Campaigns" description="Manage campaign-level ad generation and exports at scale." />
        <HStack spacing={2}>
          {/* Phase 3 placeholder — automation rules engine. The button
              is visible so the affordance is discoverable; clicking
              toasts that the feature is on the roadmap. Real
              automation lives in docs/backlog.csv. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => toast({
              title: 'Automation rules are coming',
              description: 'Cron-driven triggers + actions land in Phase 3 (see backlog).',
              status: 'info',
              duration: 3500
            })}
          >
            Automation
          </Button>
          <Tooltip label={gateTip} isDisabled={!gateDisabled} hasArrow whiteSpace="pre-line">
            {gateDisabled
              ? <Button variant="brand" size="sm" isDisabled>Generate Ads</Button>
              : <Button as={RouterLink} to="/generate-ads" variant="brand" size="sm">Generate Ads</Button>
            }
          </Tooltip>
        </HStack>
      </HStack>

      {/* ── KPI tiles ─────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <CampaignKpiTile
          label="Campaign Coverage"
          value={summary ? `${summary.campaignCoveragePct}%` : '—'}
          /* "N of M campaigns" now means campaigns that DELIVERED a creative,
             not campaigns holding Ad rows. Name the attempted-but-empty
             population so the lower number reads as truth, not data loss. */
          sub={summary
            ? `${summary.campaignsWithAds} of ${summary.totalCampaigns} campaigns delivered`
              + ((summary.campaignsAttemptedNoneDelivered ?? 0) > 0
                  ? ` · ${summary.campaignsAttemptedNoneDelivered} attempted, nothing delivered`
                  : '')
            : ''}
          progressPct={summary?.campaignCoveragePct ?? null}
          tooltip="Counts campaigns with at least one deliverable creative (rendered and, for video, titled). Failed and in-flight ads are not coverage."
        />
        <CampaignKpiTile
          label="Good Opportunities"
          value={summary?.goodOpportunities ?? '—'}
          sub="Campaigns with high opportunity score"
          tooltip="Phase 2 — opportunity scoring engine (see backlog)"
        />
        {/* Counts ATTEMPTS, deliberately unchanged — but an undifferentiated
            count reads as a success count, which is how a batch of 12 total
            failures still advanced this tile. The outcome split sits under it.
            Mirrors the same tile on pages/ProductAds. */}
        <CampaignKpiTile
          label="Ads Created"
          value={summary?.adsCreated ?? '—'}
          sub={summary && summary.adsDelivered !== undefined
            ? [
                `${summary.adsDelivered} delivered`,
                (summary.adsFailed ?? 0) > 0 ? `${summary.adsFailed} failed` : null,
                (summary.adsInFlight ?? 0) > 0 ? `${summary.adsInFlight} generating` : null
              ].filter(Boolean).join(' · ')
            : 'Total ads generated (attempts, not deliveries)'}
        />
        <CampaignKpiTile
          label="Ready to Export"
          value={summary?.adsReadyToExport ?? '—'}
          sub="Ads ready to push"
        />
      </SimpleGrid>

      {/* ── Filter bar — search + status + channel + opportunity ── */}
      <Card variant="outline">
        <CardBody py={3}>
          <HStack spacing={3} wrap="wrap">
            <InputGroup size="sm" maxW="280px" flex="1 1 220px">
              <InputLeftElement pointerEvents="none" fontSize="xs" color="brand.muted">🔍</InputLeftElement>
              <Input
                placeholder="Search campaigns…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <Select size="sm" maxW="170px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="EXPIRED">Expired</option>
            </Select>
            <Select size="sm" maxW="170px" value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}>
              <option value="">All Channels</option>
              <option value="Meta">Meta</option>
              <option value="Google">Google</option>
              <option value="TikTok">TikTok</option>
            </Select>
            <Select size="sm" maxW="210px" value={oppFilter} onChange={(e) => setOppFilter(e.target.value)}>
              <option value="">All Opportunity Scores</option>
              <option value="strong">Strong (≥70)</option>
              <option value="moderate">Moderate (40–69)</option>
              <option value="low">Low (&lt;40)</option>
              <option value="unscored">Unscored</option>
            </Select>
            <Select
              size="sm"
              maxW="210px"
              aria-label="Plan"
              data-testid="campaign-plan-filter"
              value={planScope.planFilter}
              onChange={(e) => planScope.setPlanFilter(e.target.value)}
            >
              <option value="">All plans</option>
              {planScope.plans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.title}</option>
              ))}
            </Select>
            {planScope.plansError && (
              <Text fontSize="xs" color="red.600" data-testid="campaign-plan-error">{planScope.plansError}</Text>
            )}
            {(search || statusFilter || channelFilter || oppFilter || planScope.planFilter) && (
              <Button size="sm" variant="ghost" onClick={clearFilters}>Clear filters</Button>
            )}
            <Box flex="1 1 0" />
            <Button size="sm" variant="outline" onClick={syncAll} isLoading={syncing} loadingText="Syncing…">
              Sync now
            </Button>
            <Tooltip label={gateTip} isDisabled={!gateDisabled} hasArrow whiteSpace="pre-line">
              <Button size="sm" variant="outline" onClick={newCampaignModal.onOpen} isDisabled={gateDisabled}>
                + New campaign
              </Button>
            </Tooltip>
          </HStack>
        </CardBody>
      </Card>

      {/* Legacy: gate banner + new-campaign modal */}
      <HStack justify="space-between" align="center" display="none">
        <Box />
        <Box />
      </HStack>

      {/* Account-setup gate banner — unchanged from the legacy
          layout; preserves the onboarding nudge when the brand
          isn't ad-ready yet. */}
      {gateDisabled && !readiness.loading && (
        <Card variant="outline" borderColor="orange.300" bg="orange.50">
          <CardBody py={3}>
            <VStack align="stretch" spacing={1}>
              <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
                Account setup incomplete
              </Text>
              <Text fontSize="sm" color="brand.ink">{readiness.reason}</Text>
              {readiness.blockers.length > 0 && (
                <VStack align="stretch" spacing={0.5} pt={1}>
                  {readiness.blockers.map(b => (
                    <Text key={b.code} fontSize="11px" color="brand.muted">• {b.message}</Text>
                  ))}
                </VStack>
              )}
              <HStack pt={2}>
                <Button as={RouterLink} to="/brand" variant="outline" size="xs">
                  Open onboarding status
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      <NewCampaignModal isOpen={newCampaignModal.isOpen} onClose={newCampaignModal.onClose} />

      {/* ── Table ────────────────────────────────────────────────── */}
      {loading && (
        <Card variant="outline">
          <CardBody>
            <HStack py={6} justify="center"><Spinner /><Text fontSize="sm" color="brand.muted">Loading campaigns…</Text></HStack>
          </CardBody>
        </Card>
      )}
      {error && !loading && (
        <Card variant="outline"><CardBody><Text fontSize="sm" color="red.600">{error}</Text></CardBody></Card>
      )}
      {!loading && !error && rows.length > 0 && planScope.status === 'loading' && (
        <Card variant="outline">
          <CardBody>
            <HStack py={6} justify="center">
              <Spinner />
              <Text fontSize="sm" color="brand.muted">Loading this plan’s campaigns…</Text>
            </HStack>
          </CardBody>
        </Card>
      )}
      {!loading && !error && rows.length === 0 && (
        <Card variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={3} py={8} textAlign="center">
              <Text fontSize="sm" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
                No campaigns synced yet
              </Text>
              <Text color="brand.ink" fontWeight="600" fontSize="lg">
                Connect Meta Ads or Google Ads, or create a new campaign.
              </Text>
              <HStack justify="center" spacing={2} pt={2}>
                <Button as={RouterLink} to="/brand" variant="outline" size="sm">Connect integrations</Button>
                <Tooltip label={gateTip} isDisabled={!gateDisabled} hasArrow whiteSpace="pre-line">
                  <Button onClick={newCampaignModal.onOpen} variant="brand" size="sm" isDisabled={gateDisabled}>
                    New campaign
                  </Button>
                </Tooltip>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}
      {/* Ad detail modal — opens on any expansion's thumbnail click.
          On a state change (approve / export / regenerate), invalidate
          the open campaign's expansion cache so counts rebalance. */}
      {openAd && activeBrandId && (
        <AdDetailModal
          ad={openAd}
          brandId={activeBrandId}
          onClose={() => setOpenAd(null)}
          onOpenMaster={(masterAdId) => {
            if (!activeBrandId) return;
            // Derivative + master share campaignId here (this page's
            // expansion is per-campaign, not per-product) — the master is
            // usually already in the currently-expanded campaign's ads.
            const campaignId = openAd?.campaignId || expandedId;
            const fromAdId = openAd?.adId;
            void resolveAndOpenMaster({
              masterAdId,
              brandId: activeBrandId,
              siblingAds: campaignId ? expansions[campaignId]?.ads ?? null : null,
              isCurrent: () => openAdIdRef.current === fromAdId,
              setOpenAd,
              toast
            });
          }}
          onMutated={async (updated) => {
            if (openAdIdRef.current !== updated.adId) return;
            setOpenAd(updated);
            for (const [campaignId, det] of Object.entries(expansions)) {
              if (det.ads.some(a => a.adId === updated.adId)) {
                try {
                  const res = await apiJson<CampaignAdsDetail>(
                    `/api/campaigns/${encodeURIComponent(campaignId)}/ads-detail?brandId=${encodeURIComponent(activeBrandId)}`
                  );
                  setExpansions(prev => ({
                    ...prev,
                    [campaignId]: { ...res, ads: (res.ads || []).map(toExpansionAd) }
                  }));
                } catch {
                  // best-effort refresh; modal still shows the
                  // mutated ad inline.
                }
                break;
              }
            }
          }}
        />
      )}

      {!loading && !error && planScope.status === 'error' && (
        <Card variant="outline" data-testid="campaign-plan-scope-error">
          <CardBody>
            <Text fontSize="sm" color="red.600" py={6} textAlign="center">{planScope.scopeError}</Text>
          </CardBody>
        </Card>
      )}
      {!loading && !error && planScope.status === 'filtered' && rows.length > 0 && planScope.scopedRows.length === 0 && (
        <Card variant="outline" data-testid="campaign-plan-empty">
          <CardBody>
            <Text fontSize="sm" color="brand.muted" py={6} textAlign="center">No campaigns linked to this plan.</Text>
          </CardBody>
        </Card>
      )}
      {!loading && !error && planScope.status !== 'loading' && planScope.scopedRows.length > 0 && filteredRows.length === 0 && (
        <Card variant="outline">
          <CardBody>
            <Text fontSize="sm" color="brand.muted" py={6} textAlign="center">No campaigns match these filters.</Text>
          </CardBody>
        </Card>
      )}
      {!loading && !error && planScope.status !== 'loading' && filteredRows.length > 0 && (
        <Card variant="outline">
          <CardBody p={0}>
            {/* Column header */}
            <HStack
              px={4}
              py={3}
              spacing={4}
              fontSize="11px"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.06em"
              color="brand.muted"
              borderBottomWidth="1px"
              borderColor="brand.border"
            >
              <Box flex="2.4 1 0">Campaign</Box>
              <Box flex="1.4 1 0">Coverage</Box>
              <Box flex="0.7 1 0" textAlign="center">Products</Box>
              <Box flex="0.7 1 0" textAlign="center">UGC</Box>
              <Box flex="0.7 1 0" textAlign="center">Ads</Box>
              <Box flex="0.9 1 0">Opportunity</Box>
              <Box flex="1.1 1 0">Last Activity</Box>
              <Box flex="1.6 1 0" textAlign="right">Actions</Box>
            </HStack>

            <VStack align="stretch" spacing={0} divider={<Box borderTopWidth="1px" borderColor="brand.border" />}>
              {filteredRows.map(r => (
                <CampaignRow
                  key={r.campaignId}
                  row={r}
                  planLabel={planScope.planFilter ? campaignArmLabel(r, planScope.labels) : null}
                  expanded={expandedId === r.campaignId}
                  expansion={expansions[r.campaignId] || null}
                  expansionLoading={!!expansionLoading[r.campaignId]}
                  gateDisabled={gateDisabled}
                  isRefreshing={refreshing.has(r.campaignId)}
                  onExpand={() => expandRow(r.campaignId)}
                  onGenerate={() => generateForCampaign(r.campaignId)}
                  onRefreshAds={() => refreshAdsForCampaign(r.campaignId)}
                  onView={() => navigate(`/campaigns/${encodeURIComponent(r.campaignId)}`)}
                  onOpenAd={setOpenAd}
                />
              ))}
            </VStack>

            {/* Pagination strip — purely informational for v1; no
                server-side paging yet (the brand's full set comes back
                in one query). Add real pagination if catalogs exceed
                ~200 campaigns. */}
            <HStack px={4} py={3} justify="space-between" borderTopWidth="1px" borderColor="brand.border" fontSize="11px" color="brand.muted">
              <Text>
                Showing {filteredRows.length} of {(planScope.planFilter ? planScope.scopedRows : rows).length} campaign{(planScope.planFilter ? planScope.scopedRows : rows).length === 1 ? '' : 's'}
                {filteredRows.length !== (planScope.planFilter ? planScope.scopedRows : rows).length && ' (filtered)'}
              </Text>
              <Box />
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

function CampaignKpiTile({
  label, value, sub, progressPct, tooltip
}: {
  label:        string;
  value:        number | string;
  sub?:         string;
  progressPct?: number | null;
  tooltip?:     string;
}) {
  return (
    <Card variant="outline" title={tooltip || undefined}>
      <CardBody py={3} px={4}>
        <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color="brand.muted" mb={1}>
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="800" color="brand.ink" lineHeight="1.1">
          {value}
        </Text>
        {sub && <Text fontSize="11px" color="brand.muted" mt={0.5} noOfLines={1}>{sub}</Text>}
        {typeof progressPct === 'number' && (
          <Progress value={progressPct} size="xs" colorScheme="purple" mt={2} borderRadius="md" />
        )}
      </CardBody>
    </Card>
  );
}

function CampaignRow({
  row, planLabel, expanded, expansion, expansionLoading, gateDisabled, isRefreshing,
  onExpand, onGenerate, onRefreshAds, onView, onOpenAd
}: {
  row:              CampaignRowData;
  planLabel:        string | null;
  expanded:         boolean;
  expansion:        CampaignAdsDetail | null;
  expansionLoading: boolean;
  gateDisabled:     boolean;
  isRefreshing:     boolean;
  onExpand:         () => void;
  onGenerate:       () => void;
  onRefreshAds:     () => void;
  onView:           () => void;
  onOpenAd:         (ad: ExpansionAd) => void;
}) {
  const cov = coverageLabel(row.coveragePct, { attempted: row.adCount, inFlight: row.inFlightCount });
  const status = (row.status || '').toUpperCase();
  const isActive = /ACTIVE|ENABLED/.test(status);
  const isPaused = /PAUSED/.test(status);
  const statusColor = row.isExpired ? 'red' : isActive ? 'green' : isPaused ? 'yellow' : 'gray';
  const statusLabel = row.isExpired ? 'Expired'
                    : isActive ? 'Active'
                    : isPaused ? 'Paused'
                    : (row.status || 'Unknown');

  // Primary action depends on state:
  //   paused                                   → View Campaign (read-only)
  //   active or expired + has ads              → Refresh Ads
  //   active or expired + no ads               → Generate Ads
  // Expired campaigns now allow generation + push: Meta accepts the
  // create-ad API call against an expired campaign (creatives land
  // paused) which is what we want for end-to-end push testing.
  // Paused campaigns stay read-only because Meta typically rejects
  // edits to paused-by-operator campaigns until they're resumed.
  const primaryActionLabel = isPaused ? 'View Campaign'
                           : row.adCount > 0 ? 'Refresh Ads'
                           : 'Generate Ads';
  const primaryAction = isPaused ? onView
                      : row.adCount > 0 ? onRefreshAds
                      : onGenerate;
  const primaryDisabled = gateDisabled && primaryActionLabel !== 'View Campaign';

  return (
    <Box bg={expanded ? 'rsViolet.50' : undefined}>
    <HStack
      px={4}
      py={3}
      spacing={4}
      align="center"
      cursor="pointer"
      _hover={{ bg: expanded ? undefined : 'gray.50' }}
      onClick={onExpand}
    >
      <HStack flex="2.4 1 0" spacing={3} align="center" minW={0}>
        {row.thumbUrl ? (
          <Image
            src={row.thumbUrl}
            alt={row.name}
            boxSize="48px"
            objectFit="cover"
            borderRadius="md"
            borderWidth="1px"
            borderColor="brand.border"
            flexShrink={0}
          />
        ) : (
          <Box boxSize="48px" bg="gray.100" borderRadius="md" flexShrink={0} />
        )}
        <VStack align="stretch" spacing={0} minW={0}>
          <HStack spacing={1.5}>
            <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>{row.name}</Text>
          </HStack>
          {planLabel && (
            <Text data-testid="campaign-plan-label" fontSize="10px" color="brand.muted" noOfLines={1}>{planLabel}</Text>
          )}
          <HStack spacing={1.5} mt={0.5} flexWrap="wrap">
            <Badge fontSize="9px" colorScheme={statusColor} variant="subtle">{statusLabel}</Badge>
            {row.productCount > 0 && (
              <Badge fontSize="9px" variant="outline" color="brand.muted" fontWeight="600">
                {row.productCount} product{row.productCount === 1 ? '' : 's'}
              </Badge>
            )}
            {row.channels.length > 0 && (
              <Text fontSize="10px" color="brand.muted" noOfLines={1}>{row.channels.join(', ')}</Text>
            )}
            {row.channels.length === 0 && row.platform && (
              <Text fontSize="10px" color="brand.muted">{row.platform}</Text>
            )}
          </HStack>
        </VStack>
      </HStack>
      <Box flex="1.4 1 0">
        <Text fontSize="sm" fontWeight="700" color={cov.color === 'gray' ? 'brand.muted' : 'brand.ink'}>
          {row.coveragePct}%
        </Text>
        <Progress value={row.coveragePct} size="xs" colorScheme={cov.color === 'gray' ? 'gray' : cov.color === 'orange' ? 'orange' : 'green'} mt={1} borderRadius="md" />
        <Text fontSize="10px" color="brand.muted" mt={0.5}>{cov.label}</Text>
      </Box>
      <Box flex="0.7 1 0" textAlign="center">
        <Text fontSize="sm" fontWeight="700" color="brand.ink">{row.productCount}</Text>
      </Box>
      <Box flex="0.7 1 0" textAlign="center">
        <Text fontSize="sm" fontWeight="700" color="brand.ink">{row.ugcCount}</Text>
      </Box>
      <Box flex="0.7 1 0" textAlign="center">
        <Text fontSize="sm" fontWeight="700" color="brand.ink">{row.adCount}</Text>
        {row.readyToExport > 0 && <Text fontSize="10px" color="brand.muted">{row.readyToExport} ready</Text>}
      </Box>
      <Box flex="0.9 1 0">
        {row.opportunityScore == null ? (
          <Text fontSize="11px" color="brand.muted">—</Text>
        ) : (
          <Text fontSize="sm" fontWeight="700" color={
            row.opportunityScore >= 70 ? 'green.600'
          : row.opportunityScore >= 40 ? 'orange.500'
          :                              'red.500'
          }>{row.opportunityScore}</Text>
        )}
      </Box>
      <Box flex="1.1 1 0">
        <Text fontSize="11px" color="brand.muted">{relativeTime(row.lastActivityAt)}</Text>
        <Text fontSize="10px" color="brand.muted" noOfLines={1}>{row.lastActivityLabel}</Text>
      </Box>
      <HStack flex="1.6 1 0" justify="flex-end" spacing={1} onClick={(e) => e.stopPropagation()}>
        <Button
          size="xs"
          variant="outline"
          onClick={primaryAction}
          isLoading={primaryActionLabel === 'Refresh Ads' && isRefreshing}
          isDisabled={primaryDisabled}
        >
          {primaryActionLabel}
        </Button>
        {primaryActionLabel !== 'View Campaign' && (
          <Button size="xs" variant="ghost" onClick={onView}>View</Button>
        )}
      </HStack>
    </HStack>

    {/* Inline expansion — products sidebar + ads grouped Draft /
        Approved / Exported. Same shape as the ProductAds expansion;
        sidebar lists this campaign's products instead of the
        product's campaigns. */}
    {expanded && (
      <Box px={4} pb={4} borderTopWidth="1px" borderColor="brand.border" bg="white">
        {expansionLoading && (
          <HStack py={6} justify="center">
            <Spinner size="sm" />
            <Text fontSize="sm" color="brand.muted">Loading campaign ads…</Text>
          </HStack>
        )}
        {!expansionLoading && expansion && (
          <CampaignExpansionPanel expansion={expansion} onOpenAd={onOpenAd} />
        )}
      </Box>
    )}
    </Box>
  );
}

// Per-campaign expansion content: products sidebar + ads grid grouped
// into Draft / Approved / Exported sections. Reuses the shared
// AdSectionGrid from ProductAds.
function CampaignExpansionPanel({
  expansion, onOpenAd
}: {
  expansion: CampaignAdsDetail;
  onOpenAd:  (ad: ExpansionAd) => void;
}) {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  // Defaults to the existing grouped behavior — see ProductAds' identical
  // toggle for the shared rationale.
  const [groupMode, setGroupMode] = useState<AdGroupMode>(DEFAULT_AD_GROUP_MODE);
  const filteredAds = useMemo(() => {
    if (!selectedProductId) return expansion.ads;
    return expansion.ads.filter(a => a.productId === selectedProductId);
  }, [expansion.ads, selectedProductId]);

  const grouped = useMemo(() => {
    const draft:    ExpansionAd[] = [];
    const approved: ExpansionAd[] = [];
    const exported: ExpansionAd[] = [];
    for (const ad of filteredAds) {
      const s = sectionFor(ad);
      if (s === 'draft') draft.push(ad);
      else if (s === 'approved') approved.push(ad);
      else exported.push(ad);
    }
    return { draft, approved, exported };
  }, [filteredAds]);

  if (expansion.ads.length === 0 && expansion.products.length === 0) {
    return (
      <Box py={4}>
        <Text fontSize="sm" color="brand.muted">No ads generated for this campaign yet.</Text>
      </Box>
    );
  }

  return (
    <HStack align="flex-start" spacing={4} pt={4}>
      {/* Products sidebar */}
      <VStack align="stretch" spacing={1} flex="0 0 240px">
        <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color="brand.muted" mb={1}>
          Products
        </Text>
        <ProductSidebarRow
          label="All products"
          adCount={expansion.ads.length}
          selected={selectedProductId === null}
          onClick={() => setSelectedProductId(null)}
        />
        {expansion.products.map(p => (
          <ProductSidebarRow
            key={p.productId}
            label={p.title}
            adCount={p.adCount}
            selected={p.productId === selectedProductId}
            onClick={() => setSelectedProductId(p.productId)}
          />
        ))}
      </VStack>

      {/* Ads — grouped Draft / Approved / Exported, or flat newest-first. */}
      <VStack align="stretch" flex="1 1 0" spacing={4}>
        <HStack justify="flex-end">
          <AdGroupModeToggle mode={groupMode} onChange={setGroupMode} />
        </HStack>
        {filteredAds.length === 0 && (
          <Text fontSize="sm" color="brand.muted">No ads for this product yet.</Text>
        )}
        {groupMode === 'grouped' ? (
          <>
            <AdSectionGrid label="Draft"    tint="purple" ads={grouped.draft}    onOpenAd={onOpenAd} />
            <AdSectionGrid label="Approved" tint="green"  ads={grouped.approved} onOpenAd={onOpenAd} />
            <AdSectionGrid label="Exported" tint="blue"   ads={grouped.exported} onOpenAd={onOpenAd} />
          </>
        ) : (
          // Flat newest-first: filteredAds is already API-ordered by
          // recency — no client re-sort, just skip the status split.
          <AdSectionGrid label="All" tint="gray" ads={filteredAds} onOpenAd={onOpenAd} />
        )}
      </VStack>
    </HStack>
  );
}

function ProductSidebarRow({
  label, adCount, selected, onClick
}: {
  label:    string;
  adCount:  number;
  selected: boolean;
  onClick:  () => void;
}) {
  return (
    <HStack
      px={2}
      py={2}
      spacing={2}
      borderRadius="md"
      cursor="pointer"
      bg={selected ? 'rsViolet.100' : 'transparent'}
      _hover={{ bg: selected ? 'rsViolet.100' : 'gray.50' }}
      onClick={onClick}
      justify="space-between"
    >
      <HStack spacing={2} minW={0}>
        <Box w="6px" h="6px" borderRadius="full" bg={selected ? 'rsViolet.500' : 'brand.border'} flexShrink={0} />
        <Text fontSize="13px" fontWeight={selected ? '700' : '500'} color="brand.ink" noOfLines={1}>{label}</Text>
      </HStack>
      <Text fontSize="11px" color="brand.muted" flexShrink={0}>{adCount} ad{adCount === 1 ? '' : 's'}</Text>
    </HStack>
  );
}

